
import { Component, Vue, Watch } from 'vue-property-decorator';
import * as Tone from 'tone';

interface PitchInfo {
  note: string;
  label: string;
  isSharp: boolean;
}

@Component
export default class DoremiEditorView extends Vue {
  totalSteps = 64;
  bpm = 240;
  grid: (number | null)[] = new Array(64).fill(null);
  currentStep = -1;
  isPlaying = false;
  isRepeat = false;
  lastEditedStep: number | null = null;
  lastEditedPitch: number | null = null;
  synth: Tone.Synth | null = null;
  loop: Tone.Loop | null = null;

  @Watch('$route.query.q', { immediate: true })
  onQueryChange(q: any) {
    if (typeof q === 'string' && q !== '' && q !== this.doremiString) {
      this.loadFromString(q);
    }
  }

  loadFromString(score: string) {
    const hankakuMap: { [key: string]: string } = {
      'ﾄﾞ': 'ド', 'ﾚ': 'レ', 'ﾐ': 'ミ', 'ﾌｧ': 'ファ', 'ｿ': 'ソ', 'ﾗ': 'ラ', 'ｼ': 'シ', 'ﾓ': 'モ', ',': '・'
    };
    const separetor = '/';
    const str = score.replaceAll(/(ド|レ|ミ|ファ|ソ|ラ|シ|モ|ﾄﾞ|ﾚ|ﾐ|ﾌｧ|ｿ|ﾗ|ｼ|ﾓ|・|,)/g, `${separetor}$1`);
    const arr = str.split(separetor).filter(x => x !== '');

    const joined = [
      'ド', 'レ', 'ミ', 'ファ', 'ソ', 'ラ', 'シ', 'モ', '・',
      'ﾄﾞ', 'ﾚ', 'ﾐ', 'ﾌｧ', 'ｿ', 'ﾗ', 'ｼ', 'ﾓ', ','].join('|');
    const regExp = new RegExp(`^(${joined})(\\*)?([↑↓]+)?`);

    const newGrid: (number | null)[] = new Array(this.totalSteps).fill(null);
    let currentStepIdx = 0;

    arr.forEach(x => {
      if (currentStepIdx >= this.totalSteps) return;

      const hit = x.match(regExp);
      if (!hit) return;

      const char = hit[1];
      const isHankaku = Object.keys(hankakuMap).includes(char);
      const baseChar = isHankaku ? hankakuMap[char] : char;
      const sharp = hit[2] || '';
      const octave = hit[3] || '';
      const extraLen = (x.match(/ー/g) || []).length * 2 + (x.match(/ｰ/g) || []).length * 1;
      const baseLen = isHankaku ? 1 : 2;
      const totalLen = baseLen + extraLen;

      if (baseChar !== '・') {
        const fullLabel = `${baseChar}${sharp}${octave}`;
        const pitchIdx = this.allPitches.findIndex(p => p.label === fullLabel);
        if (pitchIdx !== -1) {
          for (let i = 0; i < totalLen; i++) {
            if (currentStepIdx + i < this.totalSteps) {
              newGrid[currentStepIdx + i] = pitchIdx;
            }
          }
        }
      }
      currentStepIdx += totalLen;
    });

    this.grid = newGrid;
  }

  allPitches: PitchInfo[] = [
    { note: 'C3', label: 'ド↓', isSharp: false },
    { note: 'C#3', label: 'ド*↓', isSharp: true },
    { note: 'D3', label: 'レ↓', isSharp: false },
    { note: 'D#3', label: 'レ*↓', isSharp: true },
    { note: 'E3', label: 'ミ↓', isSharp: false },
    { note: 'F3', label: 'ファ↓', isSharp: false },
    { note: 'F#3', label: 'ファ*↓', isSharp: true },
    { note: 'G3', label: 'ソ↓', isSharp: false },
    { note: 'G#3', label: 'ソ*↓', isSharp: true },
    { note: 'A3', label: 'ラ↓', isSharp: false },
    { note: 'A#3', label: 'ラ*↓', isSharp: true },
    { note: 'B3', label: 'シ↓', isSharp: false },
    { note: 'C4', label: 'ド', isSharp: false },
    { note: 'C#4', label: 'ド*', isSharp: true },
    { note: 'D4', label: 'レ', isSharp: false },
    { note: 'D#4', label: 'レ*', isSharp: true },
    { note: 'E4', label: 'ミ', isSharp: false },
    { note: 'F4', label: 'ファ', isSharp: false },
    { note: 'F#4', label: 'ファ*', isSharp: true },
    { note: 'G4', label: 'ソ', isSharp: false },
    { note: 'G#4', label: 'ソ*', isSharp: true },
    { note: 'A4', label: 'ラ', isSharp: false },
    { note: 'A#4', label: 'ラ*', isSharp: true },
    { note: 'B4', label: 'シ', isSharp: false },
    { note: 'C5', label: 'ド↑', isSharp: false },
    { note: 'E4', label: 'モ', isSharp: false }, // モ は仮でE4で鳴らす
  ];

  get reversedPitches() {
    return [...this.allPitches].reverse();
  }

  get doremiString() {
    const hankakuMap: { [key: string]: string } = {
      'ド': 'ﾄﾞ', 'レ': 'ﾚ', 'ミ': 'ﾐ', 'ファ': 'ﾌｧ', 'ソ': 'ｿ', 'ラ': 'ﾗ', 'シ': 'ｼ', 'モ': 'ﾓ', '・': ','
    };

    let result = '';
    let i = 0;
    while (i < this.totalSteps) {
      const pitchIdx = this.grid[i];
      let count = 1;
      while (i + count < this.totalSteps && this.grid[i + count] === pitchIdx) {
        count++;
      }

      if (pitchIdx === null) {
        // 休符の処理
        if (count === 1) {
          result += ',';
        } else {
          if (count % 2 === 0) {
            result += '・';
            for (let j = 0; j < (count / 2) - 1; j++) {
              result += 'ー';
            }
          } else {
            result += '・';
            for (let j = 0; j < Math.floor(count / 2) - 1; j++) {
              result += 'ー';
            }
            result += 'ｰ';
          }
        }
      } else {
        // ノートの処理
        const pitch = this.allPitches[pitchIdx];
        const baseLabel = pitch.label.replace(/[*↑↓]/g, '');
        const decorations = pitch.label.substring(baseLabel.length);
        const hankakuBase = hankakuMap[baseLabel] || baseLabel;

        if (count === 1) {
          result += hankakuBase + decorations;
        } else {
          if (count % 2 === 0) {
            result += baseLabel + decorations;
            for (let j = 0; j < (count / 2) - 1; j++) {
              result += 'ー';
            }
          } else {
            result += baseLabel + decorations;
            for (let j = 0; j < Math.floor(count / 2) - 1; j++) {
              result += 'ー';
            }
            result += 'ｰ';
          }
        }
      }
      i += count;
    }
    return result;
  }

  mounted() {
    this.synth = new Tone.Synth().toDestination();
    Tone.Transport.bpm.value = this.bpm;
    window.addEventListener('keydown', this.handleKeyDown);
  }

  beforeDestroy() {
    this.stop();
    if (this.synth) {
      this.synth.dispose();
    }
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown(e: KeyboardEvent) {
    if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
      return;
    }
    if (this.lastEditedStep === null || this.lastEditedPitch === null) {
      return;
    }
    if (e.key === 'ArrowRight') {
      const nextStep = this.lastEditedStep + 1;
      if (nextStep < this.totalSteps) {
        this.$set(this.grid, nextStep, this.lastEditedPitch);
        this.lastEditedStep = nextStep;
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft') {
      if (this.grid[this.lastEditedStep] === this.lastEditedPitch) {
        this.$set(this.grid, this.lastEditedStep, null);
        if (this.lastEditedStep > 0) {
          this.lastEditedStep--;
        }
        e.preventDefault();
      }
    }
  }

  toggleNote(step: number, pitchIdx: number) {
    if (this.grid[step] === pitchIdx) {
      this.$set(this.grid, step, null);
      this.lastEditedStep = null;
      this.lastEditedPitch = null;
    } else {
      this.$set(this.grid, step, pitchIdx);
      this.lastEditedStep = step;
      this.lastEditedPitch = pitchIdx;
      if (this.synth) {
        this.synth.triggerAttackRelease(this.allPitches[pitchIdx].note, '16n');
      }
    }
  }

  togglePlay() {
    if (this.isPlaying) {
      this.stop();
    } else {
      this.start();
    }
  }

  async start() {
    // 再生開始時にURLを現在のメロディで更新
    if (this.$route.query.q !== this.doremiString) {
      this.$router.replace({
        query: { ...this.$route.query, q: this.doremiString }
      }).catch(() => {});
    }

    await Tone.start();
    this.currentStep = -1;
    
    this.loop = new Tone.Loop((time) => {
      const lastStep = this.currentStep;
      this.currentStep++;
      if (this.currentStep >= this.totalSteps) {
        if (this.isRepeat) {
          this.currentStep = 0;
        } else {
          Tone.Draw.schedule(() => {
            this.stop();
          }, time);
          return;
        }
      }
      const currentPitchIdx = this.grid[this.currentStep];
      const prevPitchIdx = lastStep >= 0 && lastStep < this.totalSteps ? this.grid[lastStep] : null;
      if (currentPitchIdx !== null && this.synth) {
        if (currentPitchIdx !== prevPitchIdx || this.currentStep === 0) {
          this.synth.triggerAttackRelease(this.allPitches[currentPitchIdx].note, '8n', time);
        }
      }
    }, '8n').start(0);

    Tone.Transport.start();
    this.isPlaying = true;
  }

  stop() {
    Tone.Transport.stop();
    Tone.Transport.cancel();
    if (this.loop) {
      this.loop.dispose();
      this.loop = null;
    }
    this.isPlaying = false;
    this.currentStep = -1;
  }

  clearGrid() {
    if (window.confirm('全てのノートを削除してもよろしいですか？')) {
      this.grid = new Array(this.totalSteps).fill(null);
    }
  }

  clearRow(pitchIdx: number) {
    for (let i = 0; i < this.totalSteps; i++) {
      if (this.grid[i] === pitchIdx) {
        this.$set(this.grid, i, null);
      }
    }
    // フォーカスもリセット
    if (this.lastEditedPitch === pitchIdx) {
      this.lastEditedStep = null;
      this.lastEditedPitch = null;
    }
  }

  updateBpm() {
    Tone.Transport.bpm.value = this.bpm;
  }

  copyToClipboard() {
    navigator.clipboard.writeText(this.doremiString).then(() => {
      alert('コピーしました');
    });
  }
}
